/**
 * Accuplacer test runner.
 *
 * Three sequential sections: reading → writing → writeplacer essay. Each
 * answer is POSTed to the backend as it's selected (so a tab crash doesn't
 * lose progress). Student presses "Submit test" at the end → backend
 * runs scoring → student sees ONLY a thank-you screen. No score, no review.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    BookOpen, PenTool, FileText, ChevronLeft, ChevronRight, Send,
    CheckCircle2, Loader2, AlertCircle,
} from "lucide-react";

const STORAGE_KEY = "hccbc_accuplacer_attempt_v1";
const MIN_WORDS = 300;

function fmtError(err, fallback = "Action failed.") {
    const d = err?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join("; ");
    return d.msg || JSON.stringify(d);
}

function wordCount(s) {
    return (s || "").trim().split(/\s+/).filter(Boolean).length;
}

export default function AccuplacerRunner() {
    const navigate = useNavigate();
    const location = useLocation();
    const [creds, setCreds] = useState(null);
    const [bank, setBank] = useState(null); // { reading, writing, essay, responses }
    const [section, setSection] = useState("reading"); // reading | writing | writeplacer | review
    const [qIdx, setQIdx] = useState(0);
    const [answers, setAnswers] = useState({}); // { question_id: "A" }
    const [essayText, setEssayText] = useState("");
    const [essaySubmitted, setEssaySubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    // Hydrate from localStorage or from navigation state
    useEffect(() => {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (!stored) {
            navigate("/accuplacer/test", { replace: true });
            return;
        }
        setCreds(stored);
        if (location.state?.initial) {
            // Came straight from successful login
            const init = location.state.initial;
            setBank({
                reading: init.reading,
                writing: init.writing,
                essay: init.essay,
                responses: [],
            });
        } else {
            // Refreshed — refetch
            (async () => {
                try {
                    const { data } = await api.get(
                        `/accuplacer/attempt/${stored.attempt_id}`,
                        { params: { t: stored.attempt_token } },
                    );
                    setBank({
                        reading: data.reading,
                        writing: data.writing,
                        essay: data.essay,
                        responses: data.responses,
                    });
                    // Rebuild answers map
                    const m = {};
                    (data.responses || []).forEach((r) => { m[r.question_id] = r.selected_option; });
                    setAnswers(m);
                    setEssaySubmitted(!!data.essay_submitted);
                } catch (e) {
                    toast.error(fmtError(e, "Your session has expired. Please contact the proctor."));
                    localStorage.removeItem(STORAGE_KEY);
                    navigate("/accuplacer/test", { replace: true });
                }
            })();
        }
    }, [location.state, navigate]);

    // ============================================================
    // TIER-1 ANTI-CHEAT
    // ============================================================
    // Tracks violations and auto-submits after 3 strikes. Every event is
    // POSTed to /accuplacer/attempts/{id}/proctoring-event so the admin can
    // review what happened during the test.
    const [violationCount, setViolationCount] = useState(0);
    const [violationWarn, setViolationWarn] = useState(null);

    const logEvent = useCallback(async (event_type, payload = null) => {
        if (!creds?.attempt_id) return;
        try {
            await api.post(`/accuplacer/attempts/${creds.attempt_id}/proctoring-event`, {
                event_type,
                payload,
            });
        } catch (_e) {
            // best-effort; do not interrupt the student
        }
    }, [creds]);

    const flagViolation = useCallback((event_type, msg) => {
        logEvent(event_type);
        setViolationCount((c) => {
            const next = c + 1;
            if (next >= 3) {
                setViolationWarn(
                    `Maximum proctoring violations reached. Your test is being submitted.`,
                );
            } else {
                setViolationWarn(
                    `${msg} (Warning ${next} of 3 — the next will end your test.)`,
                );
                setTimeout(() => setViolationWarn(null), 6000);
            }
            return next;
        });
    }, [logEvent]);

    // 1) Block right-click, copy, paste, cut, drag, print
    useEffect(() => {
        // We need to let the essay textarea behave normally (typing,
        // deleting, etc). Allow the standard typing keys but still log
        // copy/paste attempts. The essay textarea sets `data-essay="1"`.
        const isEssayTarget = (ev) =>
            ev.target && ev.target.matches && ev.target.matches('[data-testid="essay-textarea"]');

        const block = (ev) => {
            if (isEssayTarget(ev) && ev.type !== "contextmenu") {
                // Still log paste/copy attempts inside the essay box so the
                // proctor sees that the student tried to paste — but DO
                // prevent the actual paste (anti-plagiarism).
                if (ev.type === "paste" || ev.type === "copy" || ev.type === "cut") {
                    ev.preventDefault();
                    logEvent(`essay_${ev.type}_blocked`);
                    toast.error("Copy/paste is disabled in the essay area.");
                    return false;
                }
                return; // allow drag/etc passthrough? safer to block.
            }
            ev.preventDefault();
            logEvent("copy_paste_blocked", { type: ev.type });
            return false;
        };
        const onKey = (ev) => {
            const k = ev.key?.toLowerCase();
            const ctrl = ev.ctrlKey || ev.metaKey;
            // Always block: F12, ctrl+shift+i/j/c, ctrl+p/s/u
            const alwaysBlocked =
                k === "f12" ||
                (ctrl && ev.shiftKey && ["i", "j", "c"].includes(k)) ||
                (ctrl && ["p", "s", "u"].includes(k));
            if (alwaysBlocked) {
                ev.preventDefault();
                ev.stopPropagation();
                logEvent("shortcut_blocked", { key: k, ctrl, shift: ev.shiftKey });
                return false;
            }
            // Block ctrl+c/v/x/a OUTSIDE the essay textarea
            const copyKeys = ctrl && ["c", "v", "x", "a"].includes(k);
            if (copyKeys && !isEssayTarget(ev)) {
                ev.preventDefault();
                ev.stopPropagation();
                logEvent("shortcut_blocked", { key: k, ctrl });
                return false;
            }
        };
        const onBeforePrint = (ev) => {
            ev.preventDefault();
            logEvent("print_attempt");
            toast.error("Printing is disabled during the test.");
        };

        document.addEventListener("contextmenu", block);
        document.addEventListener("copy", block);
        document.addEventListener("cut", block);
        document.addEventListener("paste", block);
        document.addEventListener("dragstart", block);
        document.addEventListener("keydown", onKey, true);
        window.addEventListener("beforeprint", onBeforePrint);
        return () => {
            document.removeEventListener("contextmenu", block);
            document.removeEventListener("copy", block);
            document.removeEventListener("cut", block);
            document.removeEventListener("paste", block);
            document.removeEventListener("dragstart", block);
            document.removeEventListener("keydown", onKey, true);
            window.removeEventListener("beforeprint", onBeforePrint);
        };
    }, [logEvent]);

    // 2) Detect tab-blur / visibility-change → violation
    useEffect(() => {
        const onBlur = () => flagViolation("blur", "You left the test window.");
        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                flagViolation("visibility_hidden", "You switched away from the test.");
            }
        };
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [flagViolation]);

    // 3) Request fullscreen on first interaction (browsers block on load)
    useEffect(() => {
        const onFsChange = () => {
            if (!document.fullscreenElement && bank) {
                flagViolation("fullscreen_exit", "You exited full-screen mode.");
            }
        };
        document.addEventListener("fullscreenchange", onFsChange);
        return () => document.removeEventListener("fullscreenchange", onFsChange);
    }, [flagViolation, bank]);

    // 4) Disable back/forward navigation away from runner
    useEffect(() => {
        const onBeforeUnload = (ev) => {
            ev.preventDefault();
            ev.returnValue = "Are you sure you want to leave the test?";
            return ev.returnValue;
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    const enterFullscreen = useCallback(async () => {
        try {
            await document.documentElement.requestFullscreen();
        } catch (_e) { /* user-cancelled */ }
    }, []);

    // Auto-submit when violation count reaches 3
    useEffect(() => {
        if (violationCount >= 3 && bank && !done && !submitting) {
            (async () => {
                try {
                    await api.post(
                        `/accuplacer/attempt/${creds.attempt_id}/finish`,
                        {},
                        { params: { t: creds.attempt_token } },
                    );
                } catch (_e) { /* still mark done locally */ }
                localStorage.removeItem(STORAGE_KEY);
                setDone(true);
            })();
        }
    }, [violationCount, bank, done, submitting, creds]);

    const sectionQs = useMemo(() => {
        if (!bank) return [];
        if (section === "reading") return bank.reading || [];
        if (section === "writing") return bank.writing || [];
        return [];
    }, [bank, section]);

    const currentQ = sectionQs[qIdx];

    const selectAnswer = useCallback(async (qId, opt) => {
        setAnswers((prev) => ({ ...prev, [qId]: opt }));
        try {
            await api.post(
                `/accuplacer/attempt/${creds.attempt_id}/answer`,
                { question_id: qId, selected_option: opt },
                { params: { t: creds.attempt_token } },
            );
        } catch (e) {
            // Non-fatal — record locally, surface a quiet toast
            toast.error("Could not save answer — please try again.");
        }
    }, [creds]);

    const goNext = () => {
        if (qIdx < sectionQs.length - 1) {
            setQIdx(qIdx + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        // End of section — advance
        if (section === "reading") {
            setSection("writing");
            setQIdx(0);
            window.scrollTo({ top: 0 });
        } else if (section === "writing") {
            setSection("writeplacer");
            window.scrollTo({ top: 0 });
        } else if (section === "writeplacer") {
            setSection("review");
            window.scrollTo({ top: 0 });
        }
    };
    const goPrev = () => {
        if (qIdx > 0) {
            setQIdx(qIdx - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (section === "writing") {
            setSection("reading");
            setQIdx(bank.reading.length - 1);
            window.scrollTo({ top: 0 });
        } else if (section === "writeplacer") {
            setSection("writing");
            setQIdx(bank.writing.length - 1);
            window.scrollTo({ top: 0 });
        } else if (section === "review") {
            setSection("writeplacer");
            window.scrollTo({ top: 0 });
        }
    };

    const submitEssayDraft = async () => {
        if (wordCount(essayText) < MIN_WORDS) {
            toast.error(`Please write at least ${MIN_WORDS} words.`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(
                `/accuplacer/attempt/${creds.attempt_id}/essay`,
                { prompt_id: bank.essay.id, essay_text: essayText },
                { params: { t: creds.attempt_token } },
            );
            setEssaySubmitted(true);
            toast.success("Essay saved.");
        } catch (e) {
            toast.error(fmtError(e, "Could not save essay."));
        } finally {
            setSubmitting(false);
        }
    };

    const submitTest = async () => {
        setSubmitting(true);
        try {
            await api.post(
                `/accuplacer/attempt/${creds.attempt_id}/finish`,
                {},
                { params: { t: creds.attempt_token } },
            );
            localStorage.removeItem(STORAGE_KEY);
            setDone(true);
        } catch (e) {
            toast.error(fmtError(e, "Could not submit test. Please call the proctor."));
        } finally {
            setSubmitting(false);
        }
    };

    if (!bank || !creds) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#134B30]" />
            </div>
        );
    }

    // -------- Thank-you screen (no score visible to student) ----------
    if (done) {
        return (
            <div className="min-h-[80vh] bg-[#FBF5E8] flex items-center justify-center px-6 py-16" data-testid="accuplacer-test-done">
                <Card className="max-w-xl w-full p-8 text-center border-emerald-200 bg-white">
                    <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center mb-4">
                        <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <h2 className="font-display text-2xl text-[#134B30] mb-2">Thank you</h2>
                    <p className="text-sm text-[#5C6B64]">
                        Your test has been submitted. Your proctor will review your
                        responses and contact you with your placement results within
                        two business days.
                    </p>
                    <Button
                        onClick={() => navigate("/")}
                        className="mt-6 bg-[#134B30] hover:bg-[#0F3D26]"
                        data-testid="accuplacer-test-done-home"
                    >
                        Return to home
                    </Button>
                </Card>
            </div>
        );
    }


    const totalSections = 3;
    const sectionIndex = section === "reading" ? 0
        : section === "writing" ? 1
            : section === "writeplacer" ? 2 : 3;

    return (
        <div className="min-h-[80vh] bg-[#FBF5E8] py-8 px-6 select-none" style={{ userSelect: "none", WebkitUserSelect: "none" }} data-testid="accuplacer-runner">
            {/* Proctoring violation banner */}
            {violationWarn ? (
                <div className="max-w-5xl mx-auto mb-4 rounded-lg border border-rose-300 bg-rose-50 text-rose-900 px-4 py-3 flex items-start gap-2" data-testid="proctor-warning">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-medium">{violationWarn}</span>
                </div>
            ) : null}

            {/* Sticky header with progress + student name */}
            <div className="max-w-5xl mx-auto mb-4 flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520]">
                        HCC English Test · {creds.student_name}
                    </div>
                    <h1 className="font-display text-xl text-[#134B30] mt-1">
                        {section === "reading" && "Next-Generation Reading"}
                        {section === "writing" && "Next-Generation Writing"}
                        {section === "writeplacer" && "WritePlacer Essay"}
                        {section === "review" && "Review &amp; submit"}
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    {!document.fullscreenElement ? (
                        <button
                            type="button"
                            onClick={enterFullscreen}
                            className="text-[10px] uppercase tracking-wider text-[#134B30] border border-[#134B30]/40 hover:border-[#134B30] px-2 py-1 rounded transition"
                            data-testid="enter-fullscreen-btn"
                        >
                            Enter full-screen
                        </button>
                    ) : null}
                    <div className="flex items-center gap-2">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className={`h-1.5 w-12 rounded-full ${
                                    i < sectionIndex ? "bg-emerald-600"
                                        : i === sectionIndex ? "bg-[#C79520]"
                                            : "bg-[#E1DAC5]"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* MCQ sections */}
            {(section === "reading" || section === "writing") && currentQ ? (
                <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid={`mcq-${section}`}>
                    {/* Passage / sentence */}
                    {section === "reading" && currentQ.passage ? (
                        <Card className="lg:col-span-6 p-6 bg-white border-[#E1DAC5] max-h-[78vh] overflow-y-auto" data-testid="reading-passage">
                            <div className="text-[10px] uppercase tracking-wider text-[#C79520] flex items-center gap-1.5 mb-2">
                                <BookOpen className="h-3 w-3" />
                                Passage
                            </div>
                            <div className="prose prose-sm max-w-none text-[#1A201D] whitespace-pre-line leading-relaxed">
                                {currentQ.passage}
                            </div>
                        </Card>
                    ) : null}

                    <Card
                        className={`p-6 bg-white border-[#E1DAC5] ${
                            section === "reading" ? "lg:col-span-6" : "lg:col-span-12 lg:max-w-3xl lg:mx-auto"
                        }`}
                    >
                        <div className="text-[10px] uppercase tracking-wider text-[#C79520] mb-2 flex items-center gap-1.5">
                            {section === "reading" ? <BookOpen className="h-3 w-3" /> : <PenTool className="h-3 w-3" />}
                            Question {qIdx + 1} of {sectionQs.length}
                        </div>
                        {currentQ.sentence ? (
                            <div className="text-base font-serif text-[#1A201D] mb-3 leading-relaxed">
                                {currentQ.sentence}
                            </div>
                        ) : null}
                        <div className="text-sm text-[#5C6B64] mb-4">{currentQ.question_text}</div>

                        <div className="space-y-2">
                            {["A", "B", "C", "D"].map((opt) => {
                                const isSelected = answers[currentQ.id] === opt;
                                return (
                                    <button
                                        key={opt}
                                        type="button"
                                        onClick={() => selectAnswer(currentQ.id, opt)}
                                        className={`w-full text-left px-4 py-3 rounded-md border-2 transition flex items-start gap-3 ${
                                            isSelected
                                                ? "border-[#134B30] bg-[#134B30]/5"
                                                : "border-[#E1DAC5] hover:border-[#C79520]/50 bg-white"
                                        }`}
                                        data-testid={`mcq-option-${opt}`}
                                    >
                                        <div className={`h-6 w-6 rounded-full flex items-center justify-center font-display text-sm flex-shrink-0 ${
                                            isSelected ? "bg-[#134B30] text-white" : "bg-[#E1DAC5] text-[#5C6B64]"
                                        }`}>
                                            {opt}
                                        </div>
                                        <div className="text-sm text-[#1A201D] pt-0.5">{currentQ.options[opt]}</div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex items-center justify-between">
                            <Button
                                variant="outline"
                                onClick={goPrev}
                                disabled={section === "reading" && qIdx === 0}
                                className="gap-1.5"
                                data-testid="mcq-prev"
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <div className="text-xs text-[#5C6B64]">
                                {Object.keys(answers).length} answered
                            </div>
                            <Button
                                onClick={() => {
                                    // Require an answer before advancing — students cannot
                                    // skip questions by clicking Next repeatedly.
                                    if (!answers[currentQ?.id]) {
                                        toast.error("Please select an answer before continuing.");
                                        return;
                                    }
                                    goNext();
                                }}
                                disabled={!answers[currentQ?.id]}
                                className="gap-1.5 bg-[#134B30] hover:bg-[#0F3D26] disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="mcq-next"
                                title={!answers[currentQ?.id] ? "Choose an answer first" : ""}
                            >
                                {qIdx === sectionQs.length - 1
                                    ? (section === "reading" ? "Continue to Writing" : "Continue to Essay")
                                    : "Next"}
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </Card>
                </div>
            ) : null}

            {/* WritePlacer essay */}
            {section === "writeplacer" && bank.essay ? (
                <div className="max-w-5xl mx-auto" data-testid="writeplacer-section">
                    <Card className="p-6 bg-white border-[#E1DAC5]">
                        <div className="text-[10px] uppercase tracking-wider text-[#C79520] flex items-center gap-1.5 mb-2">
                            <FileText className="h-3 w-3" />
                            Essay prompt
                        </div>
                        <h3 className="font-display text-xl text-[#134B30] mb-3">{bank.essay.title}</h3>
                        <div className="bg-[#FBF5E8]/60 border border-[#E1DAC5] rounded-md p-4 text-sm text-[#1A201D] whitespace-pre-line leading-relaxed">
                            {bank.essay.prompt_text}
                        </div>
                        <div className="text-[11px] text-[#5C6B64] mt-2">
                            Target length: {bank.essay.min_words}–{bank.essay.max_words} words.
                        </div>
                    </Card>

                    <Card className="mt-4 p-6 bg-white border-[#E1DAC5]">
                        <Textarea
                            value={essayText}
                            onChange={(e) => setEssayText(e.target.value)}
                            rows={18}
                            placeholder="Write your essay here…"
                            className="font-serif text-[15px] leading-relaxed"
                            style={{ userSelect: "text", WebkitUserSelect: "text" }}
                            disabled={essaySubmitted}
                            data-testid="essay-textarea"
                        />
                        <div className="flex items-center justify-between mt-2">
                            <div className={`text-xs ${
                                wordCount(essayText) < MIN_WORDS ? "text-amber-700" : "text-emerald-700"
                            }`}>
                                {wordCount(essayText)} words {wordCount(essayText) < MIN_WORDS ? `· ${MIN_WORDS - wordCount(essayText)} more needed` : "✓"}
                            </div>
                            {essaySubmitted ? (
                                <div className="text-xs text-emerald-700 flex items-center gap-1.5">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Essay saved
                                </div>
                            ) : (
                                <Button
                                    onClick={submitEssayDraft}
                                    disabled={submitting || wordCount(essayText) < MIN_WORDS}
                                    className="gap-1.5 bg-[#134B30] hover:bg-[#0F3D26]"
                                    data-testid="essay-save"
                                >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    Save essay
                                </Button>
                            )}
                        </div>
                    </Card>

                    <div className="mt-4 flex items-center justify-between max-w-5xl">
                        <Button variant="outline" onClick={goPrev} className="gap-1.5" data-testid="essay-prev">
                            <ChevronLeft className="h-4 w-4" /> Back to writing
                        </Button>
                        <Button
                            onClick={goNext}
                            disabled={!essaySubmitted}
                            className="gap-1.5 bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] font-semibold"
                            data-testid="essay-continue"
                        >
                            Continue to review
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* Review & submit */}
            {section === "review" ? (
                <div className="max-w-2xl mx-auto" data-testid="review-section">
                    <Card className="p-8 bg-white border-[#E1DAC5] text-center">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-2">
                            Final step
                        </div>
                        <h2 className="font-display text-2xl text-[#134B30] mb-3">
                            Ready to submit your test?
                        </h2>
                        <p className="text-sm text-[#5C6B64] mb-6">
                            You have answered{" "}
                            <strong>{Object.keys(answers).length}</strong> of{" "}
                            {(bank.reading?.length || 0) + (bank.writing?.length || 0)}{" "}
                            multiple-choice questions, and your essay has been saved.
                            Once you press Submit, you cannot change your answers.
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 text-left text-sm text-amber-900 flex gap-3">
                            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <div>
                                Your responses will be reviewed by a proctor. You will
                                <strong> not </strong> see a score on this screen — your
                                results will arrive by email within two business days.
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                            <Button variant="outline" onClick={goPrev} disabled={submitting} className="gap-1.5">
                                <ChevronLeft className="h-4 w-4" /> Review essay
                            </Button>
                            <Button
                                onClick={submitTest}
                                disabled={submitting}
                                className="gap-1.5 bg-[#134B30] hover:bg-[#0F3D26] text-white"
                                data-testid="review-submit-test"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                Submit test
                            </Button>
                        </div>
                    </Card>
                </div>
            ) : null}
        </div>
    );
}
